/**
 * Users Migration Page
 *
 * Main page for managing migration users with tabs for awaiting and migrated users.
 * Includes search, pagination, and CRUD operations.
 */

import { useCallback, useState } from 'react';
import { Plus, Upload } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UsersMigrationSearch } from '@/components/users-migration-search';
import { UsersMigrationTable } from '@/components/users-migration-table';
import { UserMigrationModal } from '@/components/user-migration-modal';
import { UserMigrationDeleteDialog } from '@/components/user-migration-delete-dialog';
import { CsvImportDialog } from '@/components/csv-import-dialog';
import { PaginationControls } from '@/components/pagination-controls';
import {
  useMigrationUsers,
  useCreateMigrationUser,
  useUpdateMigrationUser,
  useDeleteMigrationUser,
} from '@/hooks/use-users-migration';
import type {
  MigrationUser,
  CreateMigrationUserRequest,
  UpdateMigrationUserRequest,
} from '@/types/user-migration';
import { useToast } from '@/hooks/use-toast';

export function Component() {
  const { toast } = useToast();

  // Tab state
  const [activeTab, setActiveTab] = useState<'awaiting' | 'migrated'>('awaiting');

  // Search and pagination state
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<MigrationUser | null>(null);

  // Delete dialog state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState<MigrationUser | null>(null);

  // CSV import dialog state
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);

  // Fetch users based on active tab
  const {
    data: usersData,
    isLoading,
    error,
    refetch,
  } = useMigrationUsers({
    page,
    limit,
    search: search || undefined,
    isMigrated: activeTab === 'migrated',
  });

  // Mutations
  const createMutation = useCreateMigrationUser();
  const updateMutation = useUpdateMigrationUser();
  const deleteMutation = useDeleteMigrationUser();

  // Extract data
  const users = usersData?.data || [];
  const pagination = usersData?.pagination;

  /**
   * Handle Tab Change
   */
  const handleTabChange = (value: string) => {
    setActiveTab(value as 'awaiting' | 'migrated');
    setPage(1); // Reset to page 1 when switching tabs
    setSearch(''); // Clear search when switching tabs
  };

  /**
   * Handle Search Change
   */
  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1); // Reset to page 1 when searching
  }, []);

  /**
   * Handle Open Create Modal
   */
  const handleOpenCreateModal = () => {
    setEditingUser(null);
    setIsModalOpen(true);
  };

  /**
   * Handle Open Edit Modal
   */
  const handleOpenEditModal = (user: MigrationUser) => {
    setEditingUser(user);
    setIsModalOpen(true);
  };

  /**
   * Handle Close Modal
   */
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
  };

  /**
   * Handle Submit (Create or Update)
   */
  const handleSubmit = async (data: CreateMigrationUserRequest | UpdateMigrationUserRequest) => {
    try {
      if (editingUser) {
        // Update existing user
        await updateMutation.mutateAsync({
          cognitoId: editingUser.cognitoId,
          data: data as UpdateMigrationUserRequest,
        });
        toast({
          title: 'User updated',
          description: 'Migration user has been updated successfully.',
        });
      } else {
        // Create new user
        await createMutation.mutateAsync(data as CreateMigrationUserRequest);
        toast({
          title: 'User created',
          description: 'Migration user has been created successfully.',
        });
      }
      handleCloseModal();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save user',
        variant: 'destructive',
      });
    }
  };

  /**
   * Handle Open Delete Dialog
   */
  const handleOpenDeleteDialog = (user: MigrationUser) => {
    setDeletingUser(user);
    setIsDeleteDialogOpen(true);
  };

  /**
   * Handle Close Delete Dialog
   */
  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setDeletingUser(null);
  };

  /**
   * Handle Confirm Delete
   */
  const handleConfirmDelete = async () => {
    if (!deletingUser) return;

    try {
      await deleteMutation.mutateAsync(deletingUser.cognitoId);
      toast({
        title: 'User deleted',
        description: 'Migration user has been deleted successfully.',
      });
      handleCloseDeleteDialog();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete user',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Users Migration</h1>
        <p className="text-muted-foreground mt-2">
          Manage users awaiting migration and view migrated users
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Awaiting Migration</CardDescription>
            <CardTitle className="text-4xl">
              {activeTab === 'awaiting' ? pagination?.total || 0 : '-'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Users waiting to be migrated to the new system
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Migrated</CardDescription>
            <CardTitle className="text-4xl">
              {activeTab === 'migrated' ? pagination?.total || 0 : '-'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Users successfully migrated to the new system
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Migration Users</CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setIsImportDialogOpen(true)}>
                <Upload className="mr-2 h-4 w-4" />
                Import CSV
              </Button>
              <Button onClick={handleOpenCreateModal}>
                <Plus className="mr-2 h-4 w-4" />
                Add User
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
            {/* Tabs List */}
            <div className="flex flex-col sm:flex-row gap-4 justify-between">
              <TabsList>
                <TabsTrigger value="awaiting">Awaiting Migration</TabsTrigger>
                <TabsTrigger value="migrated">Migrated</TabsTrigger>
              </TabsList>

              {/* Search */}
              <UsersMigrationSearch value={search} onChange={handleSearchChange} />
            </div>

            {/* Awaiting Tab Content */}
            <TabsContent value="awaiting" className="space-y-4">
              <UsersMigrationTable
                users={users}
                isLoading={isLoading}
                error={error}
                onRetry={refetch}
                onEdit={handleOpenEditModal}
                onDelete={handleOpenDeleteDialog}
              />

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <PaginationControls
                  pagination={pagination}
                  onPageChange={setPage}
                  onLimitChange={(newLimit: number) => {
                    setLimit(newLimit);
                    setPage(1);
                  }}
                />
              )}
            </TabsContent>

            {/* Migrated Tab Content */}
            <TabsContent value="migrated" className="space-y-4">
              <UsersMigrationTable
                users={users}
                isLoading={isLoading}
                error={error}
                onRetry={refetch}
                onEdit={handleOpenEditModal}
                onDelete={handleOpenDeleteDialog}
              />

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <PaginationControls
                  pagination={pagination}
                  onPageChange={setPage}
                  onLimitChange={(newLimit: number) => {
                    setLimit(newLimit);
                    setPage(1);
                  }}
                />
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <UserMigrationModal
        open={isModalOpen}
        onClose={handleCloseModal}
        user={editingUser}
        onSubmit={handleSubmit}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
        error={createMutation.error?.message || updateMutation.error?.message || null}
      />

      {/* Delete Confirmation Dialog */}
      <UserMigrationDeleteDialog
        open={isDeleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        user={deletingUser}
        onConfirm={handleConfirmDelete}
        isDeleting={deleteMutation.isPending}
      />

      {/* CSV Import Dialog */}
      <CsvImportDialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen} />
    </div>
  );
}

// Set display name for React Router
Component.displayName = 'UsersMigrationPage';
